// src/pages/SearchPage.tsx
import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Search,
  Filter,
  X,
  ChevronDown,
  Loader,
  BookOpen,
  Users,
  MessageCircle,
  Star,
  Clock,
  Grid3x3,
  List,
  Hash,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
} from "lucide-react";
import { coursesApi, showError } from "../lib";
import type {
  CourseResult,
  MentorResult,
  ForumResult,
  DiscussionResult,
  GlobalSearchResponse,
  FacetResponse,
  SearchFilters,
} from "../lib/searchApi";
import SEO from "../components/SEO";

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<GlobalSearchResponse>({
    courses: [],
    forums: [],
    discussions: [],
    mentors: [],
    pagination: { page: 1, limit: 12, total: 0, pages: 0 },
  });
  const [facets, setFacets] = useState<FacetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [activeTab, setActiveTab] = useState<"all" | "courses" | "mentors" | "forums">("all");
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    category: "",
    difficulty: "",
    priceType: "",
    minRating: 0,
  });
  const [sortBy, setSortBy] = useState("relevance");
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allResultsLoaded, setAllResultsLoaded] = useState(false);

  // Get search query from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    if (q) {
      setSearchQuery(q);
      performSearch(q, 1);
    } else {
      setLoading(false);
    }
  }, [location.search]);

  // Fetch facets on mount
  useEffect(() => {
    const fetchFacets = async () => {
      try {
        const facetsData = await coursesApi.getSearchFacets();
        setFacets(facetsData);
      } catch (err) {
        console.error("Failed to fetch facets:", err);
      }
    };
    fetchFacets();
  }, []);

  const performSearch = async (query: string, pageNum: number, isLoadMore = false) => {
    if (!query || query.length < 2) return;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const type = activeTab === "all"
        ? undefined
        : activeTab === "courses"
          ? "courses"
          : activeTab === "mentors"
            ? "mentors"
            : "forums";

      const response = await coursesApi.globalSearch(query, type, pageNum, 12);

      const newTotal = isLoadMore
        ? (results.courses.length + results.mentors.length + results.forums.length) +
          (response.courses.length + response.mentors.length + response.forums.length)
        : (response.courses.length + response.mentors.length + response.forums.length);

      if (isLoadMore) {
        setResults(prev => ({
          ...response,
          courses: [...prev.courses, ...response.courses],
          mentors: [...prev.mentors, ...response.mentors],
          forums: [...prev.forums, ...response.forums],
          pagination: response.pagination,
        }));
      } else {
        setResults(response);
      }

      setPage(response.pagination?.page || 1);
      setAllResultsLoaded(
        newTotal >= (response.pagination?.total || 0) || (response.pagination?.pages || 0) <= (response.pagination?.page || 1),
      );
    } catch (err) {
      console.error("Search error:", err);
      showError("Failed to perform search");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!loadingMore && !allResultsLoaded) {
      const nextPage = page + 1;
      setPage(nextPage);
      performSearch(searchQuery, nextPage, true);
    }
  };

  const applyFiltersAndSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) return;

    setLoading(true);
    try {
      const filters: SearchFilters = {
        category: activeFilters.category || undefined,
        difficulty: activeFilters.difficulty || undefined,
        priceType: activeFilters.priceType === "free" ? "free" : activeFilters.priceType === "paid" ? "paid" : undefined,
        rating: activeFilters.minRating > 0 ? activeFilters.minRating : undefined,
        sortBy: sortBy as SearchFilters["sortBy"],
        sortOrder: "desc",
        page: page,
        limit: 12,
      };

      const response = await coursesApi.advancedSearch(searchQuery, filters);
      setResults({
        courses: response.courses,
        forums: [],
        discussions: [],
        mentors: [],
        pagination: response.pagination,
      });
      
      const currentTotal = response.courses.length;
      const total = response.pagination?.total || 0;
      setAllResultsLoaded(currentTotal >= total);
      
    } catch (err) {
      console.error("Advanced search error:", err);
      showError("Failed to apply filters");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleFilterChange = (filterKey: string, value: string | number) => {
    setActiveFilters((prev) => ({ ...prev, [filterKey]: value }));
    setPage(1);
    setAllResultsLoaded(false);
    setTimeout(() => applyFiltersAndSearch(), 100);
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    setPage(1);
    setAllResultsLoaded(false);
    setTimeout(() => applyFiltersAndSearch(), 100);
  };

  const handleTabChange = (tab: "all" | "courses" | "mentors" | "forums") => {
    setActiveTab(tab);
    setPage(1);
    setAllResultsLoaded(false);
    performSearch(searchQuery, 1);
  };

  const clearFilters = () => {
    setActiveFilters({
      category: "",
      difficulty: "",
      priceType: "",
      minRating: 0,
    });
    setSortBy("relevance");
    setPage(1);
    setAllResultsLoaded(false);
    performSearch(searchQuery, 1);
  };

  const totalResults = results.courses.length + results.mentors.length + results.forums.length + results.discussions.length;
  const totalAvailable = results.pagination?.total || 0;

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3 h-3 ${star <= Math.floor(rating) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`}
          />
        ))}
        <span className="text-[10px] text-gray-500 ml-0.5">({rating})</span>
      </div>
    );
  };

  const renderCourseCard = (course: CourseResult) => (
    <div
      key={course._id}
      className="overflow-hidden transition-all duration-300 bg-white border border-gray-200 rounded-lg group hover:shadow-md"
    >
      <div className="relative h-32 overflow-hidden">
        <img
          src={course.thumbnail || "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=300&fit=crop"}
          alt={course.title}
          className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
        />
        {course.discount && course.discount > 0 && (
          <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1 py-0.5 rounded">
            -{course.discount}%
          </div>
        )}
        <div className="absolute bottom-1 left-1">
          <span className="px-1.5 py-0.5 bg-black/70 text-white text-[9px] rounded">
            {course.difficulty}
          </span>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
            {course.category}
          </span>
          {renderStars(course.rating)}
        </div>
        <Link to={`/course/${course._id}`}>
          <h6 className="mb-1 text-xs font-semibold text-gray-900 transition-colors line-clamp-2 hover:text-primary">
            {course.title}
          </h6>
        </Link>
        <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-2">
          <span className="flex items-center gap-0.5">
            <Users className="w-2.5 h-2.5" />
            {course.enrolledStudents}
          </span>
          <span className="flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5" />
            {course.duration || "20h"}
          </span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-xs font-bold text-primary">
            FCFA {course.price}
          </span>
          <span className="text-[10px] text-gray-400 truncate max-w-[100px]">
            {course.instructorName}
          </span>
        </div>
      </div>
    </div>
  );

  const renderMentorCard = (mentor: MentorResult) => (
    <div
      key={mentor._id}
      className="overflow-hidden transition-all duration-300 bg-white border border-gray-200 rounded-lg group hover:shadow-md"
    >
      <div className="p-3 text-center">
        <div className="w-16 h-16 mx-auto mb-2 overflow-hidden rounded-full bg-gradient-to-br from-primary/10 to-orange-100">
          {mentor.user?.avatar ? (
            <img
              src={mentor.user.avatar}
              alt={`${mentor.user.firstName || ''} ${mentor.user.lastName || ''}`.trim()}
              className="object-cover w-full h-full"
            />
          ) : (
            <Users className="w-8 h-8 mx-auto mt-4 text-primary" />
          )}
        </div>
        <h5 className="text-xs font-semibold text-gray-900 mb-0.5">
          {`${mentor.user?.firstName || ''} ${mentor.user?.lastName || ''}`.trim() || 'Expert Mentor'}
        </h5>
        <p className="text-[10px] text-primary mb-1.5">{mentor.specialty}</p>
        {renderStars(mentor.rating)}
        <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-100">
          <span className="text-[10px] text-gray-500">{mentor.sessionsCount || 0} sessions</span>
          <span className="text-xs font-bold text-primary">FCFA {mentor.pricePerSession || 3000}/hr</span>
        </div>
      </div>
    </div>
  );

  const renderForumCard = (forum: ForumResult) => (
    <div
      key={forum._id}
      className="p-3 transition-all duration-300 bg-white border border-gray-200 rounded-lg group hover:shadow-md"
    >
      <div className="flex items-start gap-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 shrink-0">
          <MessageCircle className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <Link to={`/forums/${forum._id}`}>
            <h6 className="text-xs font-semibold text-gray-900 mb-0.5 line-clamp-1 hover:text-primary transition-colors">
              {forum.title}
            </h6>
          </Link>
          <p className="text-[10px] text-gray-500 line-clamp-2 mb-1">{forum.description}</p>
          <div className="flex items-center gap-2 text-[9px] text-gray-400">
            <span className="flex items-center gap-0.5">
              <Hash className="w-2 h-2" />
              {forum.category}
            </span>
            <span className="flex items-center gap-0.5">
              <MessageCircle className="w-2 h-2" />
              {forum.topics}
            </span>
            <span className="flex items-center gap-0.5">
              <Users className="w-2 h-2" />
              {forum.memberCount}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDiscussionItem = (discussion: DiscussionResult) => (
    <div
      key={discussion._id}
      className="p-3 transition-all bg-white border border-gray-200 rounded-lg hover:shadow-md"
    >
      <div className="flex gap-2">
        <div className="flex items-center justify-center w-8 h-8 overflow-hidden bg-gray-100 rounded-full shrink-0">
          {discussion.author?.avatar ? (
            <img src={discussion.author.avatar} alt="" className="object-cover w-full h-full" />
          ) : (
            <span className="text-[10px] font-medium text-gray-600">
              {discussion.author?.name?.charAt(0) || "U"}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <Link to={`/discussion/${discussion._id}`}>
            <h6 className="text-xs font-semibold text-gray-900 mb-0.5 hover:text-primary transition-colors">
              {discussion.title}
            </h6>
          </Link>
          <p className="text-[10px] text-gray-500 line-clamp-2 mb-1">{discussion.content}</p>
          <div className="flex items-center gap-2 text-[9px] text-gray-400">
            <span>{discussion.author?.name}</span>
            <span>{discussion.replyCount} replies</span>
            <span>{discussion.likes} likes</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderListView = () => (
    <div className="space-y-2">
      {activeTab === "all" && results.discussions.map(renderDiscussionItem)}
      {activeTab === "all" && results.courses.map((course) => (
        <div key={course._id} className="p-3 transition-all bg-white border border-gray-200 rounded-lg hover:shadow-md">
          <div className="flex gap-3">
            <div className="w-20 h-16 overflow-hidden bg-gray-100 rounded-lg shrink-0">
              <img src={course.thumbnail || "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=300&fit=crop"} alt={course.title} className="object-cover w-full h-full" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">{course.category}</span>
                {renderStars(course.rating)}
              </div>
              <Link to={`/course/${course._id}`}>
                <h6 className="text-xs font-semibold text-gray-900 mb-0.5 line-clamp-1 hover:text-primary">{course.title}</h6>
              </Link>
              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                <span>{course.instructorName}</span>
                <span className="flex items-center gap-0.5">
                  <Users className="w-2.5 h-2.5" />
                  {course.enrolledStudents}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs font-bold text-primary">FCFA {course.price}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderGrid = () => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {(activeTab === "all" || activeTab === "courses") && results.courses.map(renderCourseCard)}
      {(activeTab === "all" || activeTab === "mentors") && results.mentors.map(renderMentorCard)}
      {(activeTab === "all" || activeTab === "forums") && results.forums.map(renderForumCard)}
    </div>
  );

  const hasActiveFilters = Object.values(activeFilters).some((v) => v !== "" && v !== 0);

  return (
    <>
      <SEO
        title={searchQuery ? `Search Results for "${searchQuery}"` : "Search Courses, Mentors & Forums"}
        description={searchQuery ? `Explore ${totalAvailable} results for "${searchQuery}" including expert courses, mentors, and community forums.` : "Find expert-led courses, verified mentors, and active community discussions. Search by topic, skill, or keyword to start learning."}
        keywords={searchQuery ? `${searchQuery}, online courses, mentorship, search results` : "search, find courses, find mentors, community search, learnhub search"}
      />
      <main className="min-h-screen pt-16 pb-12 bg-gray-50">
      <div className="px-3 mx-auto max-w-7xl sm:px-4">
        {/* Search Header */}
        <div className="mb-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search courses, mentors..."
                className="w-full py-2 pr-3 text-sm bg-white border border-gray-200 rounded-lg pl-9 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 text-sm text-white transition-colors rounded-lg bg-primary hover:bg-primary/90"
            >
              Search
            </button>
          </form>
        </div>

        {/* Results Summary and Controls */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          {!loading && searchQuery && (
            <p className="text-[11px] text-gray-500">
              {totalResults} of {totalAvailable} results for "{searchQuery}"
            </p>
          )}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1 px-2 py-1 text-[11px] bg-white border border-gray-200 rounded-md hover:bg-gray-50"
            >
              <Filter className="w-3 h-3" />
              Filters
              {hasActiveFilters && <span className="w-1.5 h-1.5 bg-primary rounded-full" />}
              <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>

            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="px-2 py-1 text-[11px] bg-white border border-gray-200 rounded-md focus:outline-none"
            >
              <option value="relevance">Most Relevant</option>
              <option value="rating">Highest Rated</option>
              <option value="popularity">Most Popular</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>

            <div className="flex overflow-hidden bg-white border border-gray-200 rounded-md">
              <button onClick={() => setViewMode("grid")} className={`p-1 ${viewMode === "grid" ? "bg-primary text-white" : "text-gray-500"}`}>
                <Grid3x3 className="w-3 h-3" />
              </button>
              <button onClick={() => setViewMode("list")} className={`p-1 ${viewMode === "list" ? "bg-primary text-white" : "text-gray-500"}`}>
                <List className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && facets && (
          <div className="p-3 mb-4 bg-white border border-gray-200 rounded-lg">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-medium text-gray-700">Category</label>
                <select value={activeFilters.category} onChange={(e) => handleFilterChange("category", e.target.value)} className="w-full px-2 py-1 text-[11px] border border-gray-200 rounded-md">
                  <option value="">All</option>
                  {facets.categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>{cat._id} ({cat.count})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-700">Difficulty</label>
                <select value={activeFilters.difficulty} onChange={(e) => handleFilterChange("difficulty", e.target.value)} className="w-full px-2 py-1 text-[11px] border border-gray-200 rounded-md">
                  <option value="">All</option>
                  {facets.difficulties.map((diff) => (
                    <option key={diff._id} value={diff._id}>{diff._id} ({diff.count})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-700">Price</label>
                <select value={activeFilters.priceType} onChange={(e) => handleFilterChange("priceType", e.target.value)} className="w-full px-2 py-1 text-[11px] border border-gray-200 rounded-md">
                  <option value="">All</option>
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-700">Rating</label>
                <select value={activeFilters.minRating} onChange={(e) => handleFilterChange("minRating", parseInt(e.target.value))} className="w-full px-2 py-1 text-[11px] border border-gray-200 rounded-md">
                  <option value="0">Any</option>
                  <option value="3">3+ Stars</option>
                  <option value="4">4+ Stars</option>
                </select>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-1 pt-2 mt-2 border-t border-gray-100">
                {activeFilters.category && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] bg-primary/10 text-primary rounded-full">
                    {activeFilters.category} <X className="w-2 h-2 cursor-pointer" onClick={() => handleFilterChange("category", "")} />
                  </span>
                )}
                <button onClick={clearFilters} className="text-[9px] text-red-500 ml-auto">Clear all</button>
              </div>
            )}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-4 border-b border-gray-200">
          {[
            { id: "all", label: "All", count: totalResults },
            { id: "courses", label: "Courses", count: results.courses.length },
            { id: "mentors", label: "Mentors", count: results.mentors.length },
            { id: "forums", label: "Forums", count: results.forums.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id as any)}
              className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${
                activeTab === tab.id ? "text-primary border-b-2 border-primary" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Results Display */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : totalResults === 0 ? (
          <div className="py-12 text-center bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h6 className="mb-1 text-sm font-semibold text-gray-900">No results found</h6>
            <p className="text-[11px] text-gray-500 mb-3">We couldn't find any matches for "{searchQuery}"</p>
            <button onClick={() => navigate("/courses")} className="px-3 py-1.5 text-[11px] bg-primary text-white rounded-md">Browse Courses</button>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {results.courses.length > 0 && (
                <section className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-xs font-semibold tracking-wider text-orange-600 uppercase">
                      <BookOpen className="w-4 h-4" />
                      Courses
                    </h3>
                    <button
                      onClick={() => handleTabChange("courses")}
                      className="text-[11px] font-medium text-primary hover:text-primary/80"
                    >
                      See all courses
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {results.courses.slice(0, 3).map(renderCourseCard)}
                  </div>
                </section>
              )}

              {results.mentors.length > 0 && (
                <section className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-xs font-semibold tracking-wider text-orange-600 uppercase">
                      <Users className="w-4 h-4" />
                      Mentors
                    </h3>
                    <button
                      onClick={() => handleTabChange("mentors")}
                      className="text-[11px] font-medium text-primary hover:text-primary/80"
                    >
                      See all mentors
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {results.mentors.slice(0, 3).map(renderMentorCard)}
                  </div>
                </section>
              )}

              {results.forums.length > 0 && (
                <section className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-xs font-semibold tracking-wider text-orange-600 uppercase">
                      <MessageCircle className="w-4 h-4" />
                      Forums
                    </h3>
                    <button
                      onClick={() => handleTabChange("forums")}
                      className="text-[11px] font-medium text-primary hover:text-primary/80"
                    >
                      See all forums
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {results.forums.slice(0, 3).map(renderForumCard)}
                  </div>
                </section>
              )}

              <div className="pt-2">
                <button
                  onClick={loadMore}
                  className="w-full py-2.5 text-xs font-semibold text-white transition-colors bg-orange-500 rounded-lg hover:bg-orange-600"
                >
                  View all results
                </button>
              </div>
            </div>

            {/* Load More / No More Results */}
            {!loadingMore && !allResultsLoaded && results.pagination && results.pagination.pages > page && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={loadMore}
                  className="px-4 py-2 text-[11px] font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
                >
                  Load More Results
                </button>
              </div>
            )}

            {allResultsLoaded && totalResults > 0 && totalResults >= 12 && (
              <div className="py-6 mt-4 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-[11px] text-gray-500">No more results — you've seen all {totalResults} items</span>
                </div>
              </div>
            )}

            {loadingMore && (
              <div className="flex justify-center py-4">
                <Loader className="w-5 h-5 text-primary animate-spin" />
              </div>
            )}
          </>
        )}
      </div>
      </main>
      </>
  );
};

export default SearchPage;